import { useResendFollowInvite } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionButton,
  Label,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  Navigate,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import type { MouseEventHandler } from "react"
import {
  useCallback,
  useState,
} from "react"
import { CancelInvitationDialog } from "./CancelInvitationDialog"
import { ResendInvitationDialog } from "./ResendInvitationDialog"
import styles from "./Sharing.module.scss"
import { PageTemplate } from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import {
  useDeleteFollowInvite,
  useFollowInvite,
} from "src/hooks"
import {
  CancelInvitationErrorToast,
  CancelInvitationSuccessToast,
  ResendInvitationErrorToast,
  ResendInvitationSuccessToast,
} from "src/toasts"
import {
  getSugarmateError,
  injectProps,
} from "src/utils"

const polyglotPrefix = "pages.data_sharing.manage_invitation."

export function ManageInvitation() {
  const followInviteId = useParams({
    from: "/private/data_sharing/sharing/$followInviteId/manage_invitation",
    select: (s) => s.followInviteId,
  })

  const [
    followInvite,
    hasLoaded,
  ] = useFollowInvite(followInviteId)

  const polyglot = usePolyglot()
  const navigate = useNavigate()

  const resendFollowInvite = useResendFollowInvite()
  const deleteFollowInvite = useDeleteFollowInvite()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const [
    removeInvitationDialogOpen,
    setRemoveInvitationDialogOpen,
  ] =
    useState<boolean>(false)

  const [
    resendInvitationDialogOpen,
    setResendInvitationDialogOpen,
  ] =
    useState<boolean>(false)

  const openRemoveInvitationDialog = useCallback(
    () => {
      setRemoveInvitationDialogOpen(true)
    },
    [setRemoveInvitationDialogOpen],
  )

  const openResendInvitationDialog = useCallback(
    () => {
      setResendInvitationDialogOpen(true)
    },
    [setResendInvitationDialogOpen],
  )

  const resend: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {
      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          if (!resendFollowInvite || !followInvite) {
            throw new Error("Unable to resend invitation")
          }

          await resendFollowInvite(followInviteId)
          // TODO: fireRemoveFollowerAnalytic()

          const SuccessToastComponent = injectProps(
            ResendInvitationSuccessToast,
            { email: followInvite.email },
          )

          showToast(
            "resend-invitation-success-toast",
            SuccessToastComponent,
          )
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            ResendInvitationErrorToast,
            {
              code: sugarmateError?.code,
              status: sugarmateError?.status,
            },
          )
          showToast(
            "resend-invitation-error-toast",
            ErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      resendFollowInvite,
      followInvite,
      followInviteId,
      showToast,
      setIsOverlayLoaderVisible,
    ],
  )

  const remove: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {
      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          if (!deleteFollowInvite || !followInvite) {
            throw new Error("Unable to cancel invitation")
          }

          await deleteFollowInvite(followInviteId)
          // TODO: fireRemoveFollowerAnalytic()

          const SuccessToastComponent = injectProps(
            CancelInvitationSuccessToast,
            { email: followInvite.email },
          )

          showToast(
            "cancel-invitation-success-toast",
            SuccessToastComponent,
          )

          await navigate({ to: "/data_sharing/sharing" })
        } catch {
          showToast(
            "remove-follower-error-toast",
            CancelInvitationErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      deleteFollowInvite,
      followInvite,
      followInviteId,
      showToast,
      navigate,
      setIsOverlayLoaderVisible,
    ],
  )

  if (hasLoaded && followInvite === undefined) {
    return (
      <Navigate
        to="/data_sharing/sharing"
        params={{}}
        search={{}}
        replace
      />
    )
  }

  return (
    <PageTemplate
      user="account"
      title={polyglot.t("components.page_header.pages.manage_invitation")}
      backRoutePath="/data_sharing/sharing"
    >
      <div className={styles.container}>
        <div className={styles.manageInvitationSubContainer}>
          <Label className={styles.groupLabel}>
            {polyglot.t(`${polyglotPrefix}manage_access_for.title`)}
          </Label>
          <div className={styles.manageInvitationText}>
            {followInvite?.email}
          </div>
        </div>
        <div className={styles.manageInvitationSubContainer}>
          <Label className={styles.groupLabel}>
            {polyglot.t(`${polyglotPrefix}manage_access_for_code.title`)}
          </Label>
          <div
            className={styles.manageInvitationText}
            id="follow-invitation-code"
          >
            {followInvite?.redemption_code}
          </div>
        </div>
        <div className={styles.manageInvitationActions}>
          <ActionButton
            id="resend-invitation-btn"
            className={styles.button}
            stylePreset="primary"
            onClick={openResendInvitationDialog}
          >
            {polyglot.t(`${polyglotPrefix}resend_invitation.title`)}
          </ActionButton>
          <ActionButton
            id="cancel-invitation-btn"
            className={styles.button}
            stylePreset="destructive"
            onClick={openRemoveInvitationDialog}
          >
            {polyglot.t(`${polyglotPrefix}cancel_invitation.title`)}
          </ActionButton>
        </div>
        <ResendInvitationDialog
          open={resendInvitationDialogOpen}
          setOpen={setResendInvitationDialogOpen}
          resendInvitation={resend}
          email={followInvite?.email || ""}
        />
        <CancelInvitationDialog
          open={removeInvitationDialogOpen}
          setOpen={setRemoveInvitationDialogOpen}
          cancelInvitation={remove}
          email={followInvite?.email || ""}
        />
      </div>
    </PageTemplate>
  )
}
